import { mixins } from "vue-class-component";
import { Component, Emit, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BCol, BForm, BRow } from "@/lib/typed-bootstrap";

import BillingAccount from "@/mixins/BillingAccount.mixin";
import ErrorHandling from "@/mixins/ErrorHandling.mixin";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { IAddressState, IOrgState } from "@/models/org";
import { OrgModule } from "@/store/modules/org";
import { SUPPORTED_COUNTRIES } from "@/utils";
import { IBillingAddressInputState } from "./types";
import UpdateOrg from "./UpdateOrg.mixin";
import { validateBillingAddress, validateVatId } from "./utils";

import FormInputWithLabel from "@/components/forms/inputs/FormInputWithLabel.component";
import FormSelect from "@/components/forms/inputs/FormSelect.component";

interface IBillingAccountInputState {
  id: string;
  name: string;
  billingEmail: string;
  billingAddress: IBillingAddressInputState;
  paymentProviderId: string;
  vatId: string;
  orgId: string;
}

const getDefaultAddressValues = (): IBillingAddressInputState => ({
  country: null,
  city: "",
  line1: "",
  line2: "",
  postalCode: ""
});

const mapAddressStateToInput = (
  state?: IAddressState | null
): IBillingAddressInputState =>
  state
    ? {
        ...state,
        line2: state.line2 || ""
      }
    : getDefaultAddressValues();

export const mapOrgBillingAccountToInput = (
  state: IOrgState["billingAccount"] | null
): IBillingAccountInputState => ({
  id: state?.id ?? "",
  orgId: OrgModule.id,
  name: state?.name ?? "",
  vatId: state?.vatId ?? "",
  paymentProviderId: state?.paymentProviderId ?? "",
  billingEmail: state?.billingEmail ?? "",
  billingAddress: mapAddressStateToInput(state?.billingAddress)
});

@Component
export default class BillingDataForm extends mixins(
  BillingAccount,
  UpdateOrg,
  UserPermissions,
  ErrorHandling
) {
  _tsx!: tsx.DeclareProps<{}> &
    tsx.DeclareOnEvents<{
      onBillingAccountUpdated: () => void;
    }>;
  $scopedSlots!: tsx.InnerScopedSlots<{
    "form-buttons": {
      isLoading: boolean;
      showButtons: boolean;
      onCancel: () => void;
      onSave: (ev: Event) => void;
    };
    "form-title"?: {
      title: string;
    };
  }>;

  @Emit("billingAccountUpdated") onBillingAccountUpdated() {}

  input: IBillingAccountInputState = mapOrgBillingAccountToInput(
    OrgModule.synth().billingAccount
  );
  confirmName = "";

  get showCancelSaveButton(): boolean {
    if (!this.canManageOrg) return false;

    return true;
  }

  get countries(): { text: string; value: string }[] {
    return SUPPORTED_COUNTRIES;
  }

  get orgId() {
    return OrgModule.id;
  }

  @Watch("orgId", { immediate: true })
  onOrgChange() {
    this.input = mapOrgBillingAccountToInput(OrgModule.synth().billingAccount);
  }

  onCancel() {
    this.input = mapOrgBillingAccountToInput(OrgModule.synth().billingAccount);
    this.errorMessage = "";
  }

  async handleSave(_ev: Event) {
    _ev.preventDefault();
    try {
      this.isLoading = true;
      this.errorMessage = "";
      await this.upsertBillingAccount(
        this.validatedBillingAccountInput(this.input)
      );

      this.input = mapOrgBillingAccountToInput(
        OrgModule.synth().billingAccount
      );

      this.$emit("billingAccountUpdated");
    } catch (err) {
      this.errorMessage = (err as Error).message;
    } finally {
      this.isLoading = false;
    }
  }

  validatedBillingAccountInput(input: IBillingAccountInputState) {
    if (input.name?.length <= 0) {
      throw new Error(
        this.$t("error_billing_requires_company_name").toString()
      );
    }

    if (input.billingEmail?.length <= 0) {
      throw new Error(this.$t("error_billing_requires_email").toString());
    }

    validateBillingAddress(input.billingAddress, this.$t.bind(this));
    validateVatId(
      input.billingAddress.country || "",
      input.vatId,
      this.$t.bind(this)
    );

    return {
      ...input,
      orgId: OrgModule.id
    };
  }

  render() {
    return (
      <div>
        <BAlert
          show={this.showErrorMessage}
          variant="danger"
          data-testid="error-alert"
        >
          {this.errorMessage}
        </BAlert>

        <BRow class="default__section">
          <BForm
            ref="form"
            class="default__inline-form"
            acceptCharset="UTF-8"
            role="form"
            novalidate
          >
            {this.$slots["form-title"]}

            <BRow cols="1" colsMd="2" class="mt-5">
              <BCol>
                <FormInputWithLabel
                  value={this.input.name}
                  inputType="name"
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_business_name").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_business_name"
                  ).toString()}
                  autocomplete="organization"
                  onInput={(val) => (this.input.name = val.toString())}
                  onEscapeEditing={this.onCancel}
                />
              </BCol>

              <BCol>
                <FormInputWithLabel
                  value={this.input.vatId}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_vat_id").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_vat_id"
                  ).toString()}
                  inputType="vatId"
                  onInput={(val) => (this.input.vatId = val.toString())}
                  onEscapeEditing={this.onCancel}
                />
              </BCol>
            </BRow>

            <BRow cols="1" colsMd="2">
              <BCol>
                <FormInputWithLabel
                  value={this.input.billingEmail}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_billing_email").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_business_email"
                  ).toString()}
                  inputType="email"
                  onInput={(val) => (this.input.billingEmail = val.toString())}
                  onEscapeEditing={this.onCancel}
                />
              </BCol>

              <BCol>
                <FormSelect
                  value={this.input.billingAddress.country}
                  id="org-country"
                  disabled={!this.canManageOrg}
                  label={this.$t("form_input_label_country").toString()}
                  placeholderText={this.$t(
                    "form_select_options_countries_placeholder"
                  ).toString()}
                  selectOptions={this.countries}
                  onInput={(val: string) =>
                    (this.input.billingAddress.country = val)
                  }
                />
              </BCol>
            </BRow>

            <BRow cols="1" colsMd="2">
              <BCol>
                <FormInputWithLabel
                  value={this.input.billingAddress.line1}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_line_1").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_address_line1"
                  ).toString()}
                  inputType="line1"
                  autocomplete="address-line1"
                  onInput={(val) =>
                    (this.input.billingAddress.line1 = val.toString())
                  }
                  onEscapeEditing={this.onCancel}
                />
              </BCol>

              <BCol>
                <FormInputWithLabel
                  value={this.input.billingAddress.line2}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_line_2").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_address_line2"
                  ).toString()}
                  inputType="line2"
                  autocomplete="address-line2"
                  onInput={(val) =>
                    (this.input.billingAddress.line2 = val.toString())
                  }
                  onEscapeEditing={this.onCancel}
                />
              </BCol>
            </BRow>

            <BRow cols="1" colsMd="2">
              <BCol>
                <FormInputWithLabel
                  value={this.input.billingAddress.postalCode}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_zip").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_address_postal"
                  ).toString()}
                  inputType="postalCode"
                  autocomplete="postal-code"
                  onInput={(val) =>
                    (this.input.billingAddress.postalCode = val.toString())
                  }
                  onEscapeEditing={this.onCancel}
                />
              </BCol>

              <BCol>
                <FormInputWithLabel
                  value={this.input.billingAddress.city}
                  readonly={!this.canManageOrg}
                  label={this.$t("form_input_label_city").toString()}
                  placeholderText={this.$t(
                    "form_placeholder_address_city"
                  ).toString()}
                  inputType="city"
                  autocomplete="address-level2"
                  onInput={(val) =>
                    (this.input.billingAddress.city = val.toString())
                  }
                  onEscapeEditing={this.onCancel}
                />
              </BCol>
            </BRow>

            {this.$scopedSlots["form-buttons"]?.({
              isLoading: this.isLoading,
              showButtons: this.showCancelSaveButton,
              onCancel: this.onCancel,
              onSave: this.handleSave
            })}
          </BForm>
        </BRow>
      </div>
    );
  }
}
